import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import ProductDetails from "./ProductDetails";
import PrimaryDocument from "./PrimaryDocument";
import TransactionTable from "./TransactionTable";
import { displayToast } from "../utils/Toast";
import { useSalesFormStore } from "../services/useSalesFormStore";
import { useErrorHandler } from "../services/ErrorHandler";
import Edit from "../assets/svgIcons/FormEdit.svg";
import Enquiry from "../assets/svgIcons/Enquiry.svg";
import Print from "../assets/svgIcons/FormPrint.svg";
import Copy from "../assets/svgIcons/Copy.svg";
import Delete from "../assets/svgIcons/FormDelete.svg";
import Quotation from "../assets/svgIcons/Quotation.svg";
import Loader from "./Loader";
import { getFieldMaster } from "../apis/fieldMaster";
import {
  fetchProductMasterTransactionData,
  fetchProductMasterTransactionByWoNo,
  deleteTransactionByCode,
} from "../apis/transaction";
import { usePermissions } from "../hooks/usePermissions";
import ConfirmationModal from "./ConfirmationModal";
import "../styles/pages/formBulder/SalesForm.css";

const PackingList = () => {
  const { tab: urlTab, category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const company = location.state?.company;
  const productData = location.state?.productData ?? {};
  const workOrderNoFromNavigation = location.state?.woNo ?? "";
  // const isCreatedViewMode = location.state?.isViewMode ?? {};
  const {
    transactionId,
    rowData: rowDataFromQuotation,
    dynamicHeadData,
    dynamicItemData,
    dynamicTransaction,
    documentFileName,
  } = location.state || {};
  const transactionCode = "PL";
  const {
    selectedPickUpTransaction,
    selectedTransaction,
    selectedPickUpTransactionItems,
    clearTransactionState,
  } = useSalesFormStore();
  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const [tableData, setTableData] = useState({
    head_fields: [],
    rows: [],
    extra_forms: [],
  });
  const [transactionData, setTransactionData] = useState({
    companyData: {},
    headDetails: {},
    itemDetails: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCleared, setIsCleared] = useState(false);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [fetchedTranId, setFetchedTranId] = useState(null);

  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails
  );
  const setTransactionItems = useSalesFormStore(
    (state) => state.setTransactionItems
  );
  const clearForm = useSalesFormStore((state) => state.clearForm);

  const rights = usePermissions(transactionCode);

  useEffect(() => {
    if (rowDataFromQuotation) {
      setIsCopyMode(true);
      setIsEditMode(false);

      const headDetails = rowDataFromQuotation.head_data || {};
      const itemDetails = rowDataFromQuotation.item_data || [];

      setTransactionData({
        companyData: {},
        headDetails,
        itemDetails,
      });

      setPrimaryDetails(headDetails);
      setTransactionItems(itemDetails);

      displayToast(
        "Quotation data loaded. You can modify and save as new.",
        "info"
      );
    }
  }, [rowDataFromQuotation]);

  useEffect(() => {
    if (!productData.tran_id) {
      clearForm();
      clearTransactionState();
      setIsCleared(true);
    } else {
      setIsCleared(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (!transactionId && workOrderNoFromNavigation) {
      setPrimaryDetails({ design_workorder: String(workOrderNoFromNavigation) });
    }
  }, [transactionId, workOrderNoFromNavigation, setPrimaryDetails]);

  useEffect(() => {
    if (transactionCode) {
      fetchFieldMaster();
      clearForm();
    }
  }, [transactionCode]);

  useEffect(() => {
    if (productData && transactionCode && productData.tran_id) {
      fetchFieldMasterTransactionDataHandler(transactionCode, productData.tran_id);
    } else if (transactionCode && workOrderNoFromNavigation) {
      fetchFieldMasterTransactionByWoNoHandler(transactionCode, workOrderNoFromNavigation);
    }
  }, [productData, transactionCode]);

  useEffect(() => {
    if (
      isCleared &&
      selectedPickUpTransaction &&
      selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id
    ) {
      const id =
        selectedPickUpTransaction[selectedTransaction.tran_code]?.tran_id;

      fetchFieldMasterTransactionDataHandler(transactionCode, productData.tran_id);
    }
  }, [selectedPickUpTransaction, isCleared]);

  const processAndApplyDynamicData = () => {
    if (!dynamicTransaction) {
      return;
    }

    if (dynamicTransaction.head_fields && dynamicHeadData) {
      const updatedHeadDetails = { ...transactionData.headDetails };

      dynamicTransaction.head_fields.forEach((mapping) => {
        const sourceField = mapping.field_name;
        const targetField = mapping.copy_to_field_name;

        if (dynamicHeadData[sourceField] !== undefined) {
          updatedHeadDetails[targetField] = dynamicHeadData[sourceField];
        }
      });

      setTransactionData((prev) => ({
        ...prev,
        headDetails: updatedHeadDetails,
      }));
      setPrimaryDetails(updatedHeadDetails);
    }

    if (
      dynamicTransaction.item_fields &&
      Array.isArray(dynamicItemData) &&
      dynamicItemData.length > 0
    ) {
      let updatedItemDetails = [];

      updatedItemDetails = dynamicItemData.map((sourceItem) => {
        const newItem = {};

        dynamicTransaction.item_fields.forEach((mapping) => {
          const sourceField = mapping.field_name;
          const targetField = mapping.copy_to_field_name;

          if (sourceItem[sourceField] !== undefined) {
            newItem[targetField] = sourceItem[sourceField];
          }
        });

        return newItem;
      });

      setTransactionData((prev) => ({
        ...prev,
        itemDetails: updatedItemDetails,
      }));
      setTransactionItems(updatedItemDetails);
    }

    displayToast("Dynamic data has been applied", "info");
  };

  useEffect(() => {
    if (
      dynamicTransaction &&
      (dynamicHeadData || (dynamicItemData && dynamicItemData.length > 0))
    ) {
      processAndApplyDynamicData();
    }
  }, [dynamicTransaction, dynamicHeadData, dynamicItemData]);

  const handleCloseEditMode = () => {
    setIsCopyMode(false);
    setIsEditMode(false);
    if (transactionCode && productData.tran_id) {
      fetchFieldMasterTransactionDataHandler(transactionCode, productData.tran_id);
    } else if (transactionCode && workOrderNoFromNavigation) {
      fetchFieldMasterTransactionByWoNoHandler(transactionCode, workOrderNoFromNavigation);
    }
  };

  const fetchFieldMasterTransactionDataHandler = async (
    transactionCode,
    productDataId
  ) => {
    setPrimaryDetails({});

    setIsLoading(true);
    try {
      const response = await fetchProductMasterTransactionData(
        transactionCode,
        productDataId
      );

      if (response.description) {
        // If product_id lookup fails and we have a WO number, try by WO number
        if (workOrderNoFromNavigation) {
          await fetchFieldMasterTransactionByWoNoHandler(transactionCode, workOrderNoFromNavigation);
          return;
        }
        displayToast(response.description, "error");
      } else {
        const companyData = response.company_data || {};
        const headDetails = response.head_data || {};
        const itemDetails = response.item_data || [];

        if (productDataId) {
          setTransactionData({
            companyData,
            headDetails,
            itemDetails,
          });
          setPrimaryDetails(headDetails);
          setTransactionItems(itemDetails);
          if (headDetails.tran_id) {
            setFetchedTranId(headDetails.tran_id);
          }
        } else if (selectedPickUpTransactionItems?.length > 0) {
          setPrimaryDetails(headDetails);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMasterTransactionByWoNoHandler = async (
    transactionCode,
    woNo
  ) => {
    setPrimaryDetails({});

    setIsLoading(true);
    try {
      const response = await fetchProductMasterTransactionByWoNo(
        transactionCode,
        woNo
      );

      if (response.description) {
        // No existing PL for this WO — this is fine for first-time creation
        setFetchedTranId(null);
      } else {
        const companyData = response.company_data || {};
        const headDetails = response.head_data || {};
        const itemDetails = response.item_data || [];

        setTransactionData({
          companyData,
          headDetails,
          itemDetails,
        });
        setPrimaryDetails(headDetails);
        setTransactionItems(itemDetails);
        if (headDetails.tran_id) {
          setFetchedTranId(headDetails.tran_id);
        }
      }
    } catch (error) {
      // Silent fail — no existing PL found, user can create new
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);

        displayToast(response.description, "error");
      } else {
        const headFields = response.items?.head_fields || [];
        const rows = response.items?.item_fields || [];
        const extraForms = response.items?.extra_forms || [];

        const updatedHeadFields = [...headFields];
        setTableData({
          head_fields: updatedHeadFields,
          rows,
          extra_forms: extraForms,
        });
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintClick = () => {
    if (!rights.can_print) {
      displayToast(`You don't have permission to print ${category}`, "error");
      return;
    }

    if (
      !transactionData.headDetails ||
      Object.keys(transactionData.headDetails).length === 0
    ) {
      displayToast("No transaction data available to print", "error");
      return;
    }

    const fileName = documentFileName || "invoice";

    navigate(`/${tab}/${category}/transactions/${fileName}`, {
      state: {
        formData: {
          headDetails: transactionData.headDetails,
          itemDetails: transactionData.itemDetails,
          companyData: transactionData.companyData,
          rowData: tableData?.rows || [],
        },
      },
    });
  };

  const handleCopyClick = () => {
    setIsCopyMode(true);
    setIsEditMode(false);
    setPrimaryDetails(transactionData.headDetails);
    setTransactionItems(transactionData.itemDetails);
    displayToast(
      "Form is now in copy mode. You can modify and save as a new transaction.",
      "info"
    );
  };

  const handleEditClick = () => {
    if (!rights.can_edit) {
      displayToast(`You don't have permission to edit ${category}`, "error");
      return;
    }

    setIsEditMode(true);
    setIsCopyMode(false);
    setPrimaryDetails(transactionData.headDetails);
    setTransactionItems(transactionData.itemDetails);
    displayToast(
      "Form is now in edit mode. You can modify and update the transaction.",
      "info"
    );
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(`You don't have permission to delete ${category}`, "error");
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(
        transactionCode,
        transactionId || fetchedTranId
      );

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigate(`/${tab}/${category}`, {
          state: { transactionCode, forceRefresh: true },
        });
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      displayToast("Failed to delete transaction", "error");
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const effectiveTransactionId = transactionId || fetchedTranId;
  const isViewMode = Boolean(effectiveTransactionId) && !isCopyMode && !isEditMode;

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="sales-form-container">
          <header className="sales-form-header">
            <div className="sales-form-header-heading">
              <button
                className="back-button-transaction"
                onClick={handleBackClick}
              >
                <ArrowLeft size={25} />
              </button>
              <img
                src={category === "enquiry" ? Enquiry : Quotation}
                className="sales-form-header-icon"
                alt="Form Type"
              />
              <h5>
                Packaging List {isCopyMode && " - Copy Mode"}
                {isEditMode && " - Edit Mode"}
              </h5>
            </div>
            <div className="sales-form-header-heading">
              {isCopyMode || isEditMode ? (
                <X
                  className="sales-form-icons cursor-pointer"
                  onClick={handleCloseEditMode}
                  size={24}
                  title="Close Mode"
                />
              ) : isViewMode ? (
                <img
                  src={Edit}
                  className="sales-form-icons"
                  alt="Edit"
                  onClick={handleEditClick}
                  style={{
                    cursor: rights.can_edit ? "pointer" : "not-allowed",
                    opacity: rights.can_edit ? 1 : 0.5,
                  }}
                  title={
                    rights.can_edit
                      ? "Edit Transaction"
                      : "No permission to edit"
                  }
                />
              ) : (
                transactionCode &&
                effectiveTransactionId && (
                  <>
                    <img
                      src={Copy}
                      className="sales-form-icons"
                      alt="Copy"
                      onClick={handleCopyClick}
                      style={{
                        cursor: rights.can_add ? "pointer" : "not-allowed",
                        opacity: rights.can_add ? 1 : 0.5,
                      }}
                      title={
                        rights.can_add
                          ? "Copy Transaction"
                          : "No permission to copy"
                      }
                    />
                    <img
                      src={Edit}
                      className="sales-form-icons"
                      alt="Edit"
                      onClick={handleEditClick}
                      style={{
                        cursor: rights.can_edit ? "pointer" : "not-allowed",
                        opacity: rights.can_edit ? 1 : 0.5,
                      }}
                      title={
                        rights.can_edit
                          ? "Edit Transaction"
                          : "No permission to edit"
                      }
                    />
                    <img
                      src={Delete}
                      className="sales-form-icons"
                      alt="Delete"
                      onClick={handleDeleteClick}
                      style={{
                        cursor: rights.can_delete ? "pointer" : "not-allowed",
                        opacity: rights.can_delete ? 1 : 0.5,
                      }}
                      title={
                        rights.can_delete
                          ? "Delete Transaction"
                          : "No permission to delete"
                      }
                    />
                    <img
                      src={Print}
                      className="sales-form-icons"
                      onClick={handlePrintClick}
                      style={{
                        cursor: rights.can_print ? "pointer" : "not-allowed",
                        opacity: rights.can_print ? 1 : 0.5,
                      }}
                      title={
                        rights.can_print
                          ? "Print Transaction"
                          : "No permission to print"
                      }
                      alt="Print"
                    />
                  </>
                )
              )}
            </div>
          </header>
          <div className="sales-form-content">
            <ProductDetails
              tab={tab}
              company={company ? company : transactionData.companyData}
              isEditMode={isEditMode}
              productData={productData}
            />

            <PrimaryDocument
              transactionCode={transactionCode}
              headFields={tableData.head_fields}
              extraForms={tableData.extra_forms}
              isViewMode={isViewMode}
              isCopyMode={isCopyMode}
              isAutoTransactionNo={true}
            />
          </div>
          <TransactionTable
            rowData={tableData?.rows || []}
            transactionCode={transactionCode || ""}
            isViewMode={isViewMode}
            initialData={
              isCopyMode || isEditMode ? transactionData.itemDetails : undefined
            }
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            headFields={tableData.head_fields}
            locationState={location.state}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default PackingList;
